import { PageWrapper } from 'components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { useGetMerchantQuery } from 'services/merchants';
import { MerchantPayoutFlow } from 'services/merchants/types';

import IconBuild from 'components/icons/build';
import IconCrypto from 'components/icons/crypto';
import { IconPayoutReserve } from 'components/icons/payoutReserve';
import IconPieChart from 'components/icons/pieChart';
import IconProfile from 'components/icons/profile';
import IconRefundAlt from 'components/icons/refundAlt';
import LoadingIndicator from 'components/loadingIndicator/loadingIndicator';
import { TabItem } from 'components/tabs';

import RoutePath from 'router/path';

import selectAccount, { selectAccountAccess } from 'state/selectors/accounts/accountSelector';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountAccessLevel } from 'types/account';
import { UserAccessLevel } from 'types/user';

import { isRefundSupportedMerchantCurrency } from 'utils/refunds';

import { AccountSettings } from './accountSettings';
import { KEYS, LABELS } from './keys';

export const MerchantViewContainer = () => {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  const { t } = useTranslation();
  const navigate = useNavigate();
  const matchId = useMatch(KEYS.ROUTE_MATCH_ID);
  const match = useMatch(KEYS.ROUTE_MATCH);
  const selectedMatch = matchId || match;
  const { account } = useSelector(selectAccount);
  const { route } = selectedMatch?.params || {};
  const params = useParams();
  const uuid = params.uuid || account.uuid;
  const merchantUuidInUrl = selectedMatch?.params?.uuid ?? params.uuid;

  const navigateToMerchantTab = (tabSegment: string) => {
    if (merchantUuidInUrl) {
      navigate(`${RoutePath.Pwc.Accounts.Root}/${merchantUuidInUrl}/${tabSegment}`);
      return;
    }
    navigate(`${RoutePath.Pwc.AccountSettings.Root}/${tabSegment}`);
  };

  const { data: merchantResponse, isLoading } = useGetMerchantQuery(uuid!, {
    skip: !uuid,
  });

  const userLevel = useSelector(selectUserLevel);
  const accountAccessLevel = useSelector(selectAccountAccess);
  const isSupport = userLevel === UserAccessLevel.Support;
  const isMerchantViewOnly = accountAccessLevel === AccountAccessLevel.Viewer;
  const isIndividualMerchant = merchantResponse?.data.payoutFlow === MerchantPayoutFlow.DIRECT;
  const isRefundMerchantCurrencySupported = isRefundSupportedMerchantCurrency(merchantResponse?.data?.mainFiatCurrency);
  const isCryptoPayoutEnabled = !!merchantResponse?.data?.cryptoPayoutEnabled;
  const isPwcEnabled = !!merchantResponse?.data?.pwcEnabled;

  // remove that check when refund tab should be available for all environments.
  const isRefundAccountTabAvailable = window.s4cConfig.features?.refund === true;

  const tabs: TabItem[] = [
    {
      key: KEYS.TABS.GENERAL,
      label: t(LABELS.TABS.GENERAL),
      icon: <IconBuild />,
      onClick: () => navigate(merchantUuidInUrl ? RoutePath.pwc.accountById(merchantUuidInUrl) : RoutePath.Pwc.AccountSettings.General),
    },
    ...(!isSupport && !isMerchantViewOnly && isPwcEnabled
      ? [
          {
            key: KEYS.TABS.SETTLEMENT,
            label: t(LABELS.TABS.SETTLEMENT),
            icon: <IconRefundAlt />,
            onClick: () => navigateToMerchantTab(RoutePath.Pwc.AccountSettings.Settlement),
          },
        ]
      : []),
    ...(!isSupport && !isMerchantViewOnly
      ? [
          {
            key: KEYS.TABS.API_KEYS,
            label: t(LABELS.TABS.API_KEYS),
            icon: <IconCrypto />,
            onClick: () => navigateToMerchantTab(RoutePath.Pwc.AccountSettings.APIKeys),
          },
        ]
      : []),
    {
      key: KEYS.TABS.USER_MANAGEMENT,
      label: t(LABELS.TABS.USER_MANAGEMENT),
      icon: <IconProfile />,
      onClick: () => navigateToMerchantTab(RoutePath.Pwc.AccountSettings.Users),
    },
    ...(!isIndividualMerchant && isPwcEnabled && isRefundMerchantCurrencySupported && isRefundAccountTabAvailable
      ? [
          {
            key: KEYS.TABS.REFUND_ACCOUNT,
            label: t(LABELS.TABS.REFUND_ACCOUNT),
            icon: <IconPieChart />,
            onClick: () => navigateToMerchantTab(RoutePath.Pwc.AccountSettings.RefundAccount),
          },
        ]
      : []),
    ...(!isIndividualMerchant && isCryptoPayoutEnabled
      ? [
          {
            key: KEYS.TABS.PAYOUT_RESERVE,
            label: t(LABELS.TABS.PAYOUT_RESERVE),
            icon: <IconPayoutReserve />,
            onClick: () => navigateToMerchantTab(RoutePath.Pwc.AccountSettings.PayoutReserve),
          },
        ]
      : []),
  ];

  const selectedTab: TabItem | undefined = tabs.find((tab) => tab.key === route || tab.key === lastSegment);

  const breadcrumbsStart = uuid
    ? LABELS.S4C_USER_BREADCRUMBS.map((item) => t(item))
    : LABELS.PARTNER_USER_BREADCRUMBS.map((item) => t(item));

  const breadcrumbs = [...breadcrumbsStart, selectedTab?.label].filter(Boolean) as string[];

  if (isLoading) {
    return (
      <PageWrapper pageTitle={t(LABELS.PAGE_TITLE)} breadcrumbs={breadcrumbs}>
        <LoadingIndicator padded />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper pageTitle={t(LABELS.PAGE_TITLE)} breadcrumbs={breadcrumbs}>
      <AccountSettings tabs={tabs} selected={selectedTab} />
    </PageWrapper>
  );
};
